// DocumentVerification.jsx
import React, { useState, useEffect } from 'react';
import { 
  User, 
  FileCheck, 
  Home, 
  Calendar, 
  AlertCircle,
  FileText,
  MapPin,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import './DocumentVerification.css';
import { useParams } from "react-router";
import {formatCurrency, formatDateIntl} from "../../util/currency_lookup";
import StatusUpdateModal from '../StatusUpdateModal';
import '../StatusUpdateModal.css';


const DocumentVerification = () => {
  const [verification, setVerification] = useState(null);
  const [document, setDocument] = useState(null);
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatusType, setSelectedStatusType] = useState(null);

  const { id } = useParams();

  useEffect(() => {
    fetchVerificationDetails();
  }, []);

  const fetchVerificationDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch verification details
      const verificationResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/verification-by/`+id, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!verificationResponse.ok) throw new Error('Failed to fetch verification details');
      const verificationData = await verificationResponse.json();
      setVerification(verificationData);

      // Fetch document details
      const documentResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/document-by/${verificationData.document_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!documentResponse.ok) throw new Error('Failed to fetch document details');
      const documentData = await documentResponse.json();
      console.log(documentData);
      setDocument(documentData);

      // Fetch address details
      const addressResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/address-by/${verificationData.address_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!addressResponse.ok) throw new Error('Failed to fetch address details');
      const addressData = await addressResponse.json();
      setAddress(addressData);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleStatusClick = (type, status) => {
    if (status.toLowerCase() === 'pending') {
        console.log("<<<<<>>>>>>>");
      setSelectedStatusType(type);
      setIsModalOpen(true);
    }
  };
  
  // Add this handler for status update
  const handleStatusUpdate = async (newStatus) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/verification/status`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: selectedStatusType,
        status: newStatus,
        verification_id: verification.document_id // or whatever your ID field is
      })
    });
  
    if (!response.ok) {
      throw new Error('Failed to update status');
    }
  
    // Refresh the page data
    await fetchVerificationDetails();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      verified: { icon: CheckCircle, class: 'status-verified' },
      pending: { icon: Clock, class: 'status-pending' },
      rejected: { icon: XCircle, class: 'status-rejected' }
    };
    return statusMap[status.toLowerCase()] || statusMap.pending;
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!verification) return <div className="error">No verification data found</div>;

    <StatusUpdateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={selectedStatusType}
        onSubmit={handleStatusUpdate}
    />

  return (
    <div className="verification-detail">
      <div className="header">
        <div className="header-content" onClick={() => handleStatusClick('kyc', verification.kyc_status)}>
          <h1>Verification Details</h1>
          <div className={`status ${verification.kyc_status.toLowerCase()}`}>
            {verification.kyc_status}
          </div>
        </div>
        <div className="verification-id">ID: #{verification.document_id}</div>
      </div>

      {verification.rejected_reason && (
        <div className="rejection-notice">
          <AlertCircle className="icon" />
          <span>{verification.rejected_reason}</span>
        </div>
      )}

      <div className="details-grid">
        <div className="detail-card">
          <div className="detail-card-content">
            <User className="detail-icon" />
            <div>
              <div className="detail-label">Personal Information</div>
              <div className="detail-value">
                {verification.first_name} {verification.last_name}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <div className="detail-card-content">
            <Calendar className="detail-icon" />
            <div>
              <div className="detail-label">Verification Date</div>
              <div className="detail-value">
                {formatDateIntl(verification.verification_date)}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <div className="detail-card-content"
            onClick={() => handleStatusClick('kyc', verification.kyc_status)}
          >
            <FileCheck className="detail-icon" />
            <div>
              <div className="detail-label">Document Status</div>
              <div className={`detail-value status ${verification.document_status.toLowerCase()}`}>
                {verification.document_status}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <div className="detail-card-content"
            onClick={() => handleStatusClick('kyc', verification.kyc_status)}
          >
            <Home className="detail-icon" />
            <div>
              <div className="detail-label">Address Status</div>
              <div className={`detail-value status ${verification.address_status.toLowerCase()}`}>
                {verification.address_status}
              </div>
            </div>
          </div>
        </div>
      </div>

      {document && (
        <div className="section">
          <h2>
            <FileText className="section-icon" />
            Document Information
          </h2>
          <div className="details-content">
            <div className="detail-row">
              <div className="detail-label">Document Type</div>
              <div className="detail-value">{document.document_type}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Document Number</div>
              <div className="detail-value">{document.document_number}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Issue Country</div>
              <div className="detail-value">{document.issue_country}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Expiration Date</div>
              <div className="detail-value">{formatDateIntl(document.expiration_date)}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Submission Date</div>
              <div className="detail-value">{formatDateIntl(document.submission_date)}</div>
            </div>
          </div>
        </div>
      )}

      {address && (
        <div className="section">
          <h2>
            <MapPin className="section-icon" />
            Address Information
          </h2>
          <div className="details-content">
            <div className="detail-row">
              <div className="detail-label">Address Line 1</div>
              <div className="detail-value">{address.address_line1}</div>
            </div>
            {address.address_line2 && (
              <div className="detail-row">
                <div className="detail-label">Address Line 2</div>
                <div className="detail-value">{address.address_line2}</div>
              </div>
            )}
            <div className="detail-row">
              <div className="detail-label">City</div>
              <div className="detail-value">{address.city}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">State</div>
              <div className="detail-value">{address.state}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Postal Code</div>
              <div className="detail-value">{address.postal_code}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Country</div>
              <div className="detail-value">{address.country}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentVerification;
